import * as React from 'react';
import { CSSProperties } from 'react';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import { ItemTableData, ItemTableDataRow, ItemTableDataField } from './ItemTableData';
import { getQuoteDocumentStorageUrl } from '../../application/Models/QuoteDocument';
import { QuoteDocument } from 'types/document.type';
import { Checkbox } from '@mui/material';
import { WithStyles } from '@mui/styles';
import styles from './styles';

export interface ItemTableDataProps {
    data: ItemTableData;
    enableDeletedRows?: boolean;
    onRowClick?: (dataRow: ItemTableDataRow, index: number) => void;
    defaultHeaderCellStyle?: CSSProperties;
    defaultBodyCellStyle?: CSSProperties;
    enableSelectRow?: boolean;
    onSelectedChanged?: (selectedKeys: string[]) => void;
}

interface ItemTableDataState {
    order: 'asc' | 'desc';
    orderBy: number;
    enableSorting?: boolean;
    allChecked: boolean;
    indeterminateChecked: boolean;
    itemSelected: boolean[];
}

function parseValue(field: ItemTableDataField) {
    if (!field || !field.value) {
        return null;
    }
    let value: any = field.value;
    if (field.format && field.format === 'currency') {
        value = (value as string).replace(/,/g, '');
        value = (value as string).replace('$', '');
    }
    if (field.format && (field.format === 'currency' || field.format === 'number')) {
        value = Number(value);
    } else if (field.format && field.format === 'date') {
        value = new Date(value);
    } else if (typeof value === 'string') {
        value = value.toLowerCase();
    }
    return value;
}

function desc(a: ItemTableDataField[], b: ItemTableDataField[], orderBy: number) {
    const aValue = parseValue(a[orderBy]);
    const bValue = parseValue(b[orderBy]);

    if (bValue < aValue) {
        return -1;
    }
    if (bValue > aValue) {
        return 1;
    }
    return 0;
}

function stableSort(array: ItemTableDataRow[], cmp: (a: ItemTableDataField[], b: ItemTableDataField[]) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [ItemTableDataRow, number]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0].fields, b[0].fields);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

function getSorting(order: string, orderBy: number) {
    return order === 'desc'
        ? (a: ItemTableDataField[], b: ItemTableDataField[]) => desc(a, b, orderBy)
        : (a: ItemTableDataField[], b: ItemTableDataField[]) => -desc(a, b, orderBy);
}

function isDocumentType(value: any): value is QuoteDocument {
    return value ? !!value.fileName || value.fileName === '' : value;
}

function isQuoteDocument(value: any): value is QuoteDocument {
    return !!(value as QuoteDocument).uuid;
}

class ItemTable extends React.Component<ItemTableDataProps & WithStyles<typeof styles>, ItemTableDataState> {
    constructor(props: ItemTableDataProps & WithStyles<typeof styles>) {
        super(props);

        this.state = {
            order: props.data.initialSortOrder ? props.data.initialSortOrder : 'asc',
            orderBy: props.data.initialSortColumnIndex ? props.data.initialSortColumnIndex : 0,
            enableSorting: props.data.enableSorting,
            allChecked: false,
            indeterminateChecked: false,
            itemSelected: new Array(props.data.body.length),
        };

        this.state.itemSelected.fill(false);
    }

    orderSetCallback = (sortIndex: number) => {
        const { orderBy, order } = this.state;
        const isDesc = orderBy !== sortIndex || order === 'desc';
        this.setState({
            order: isDesc ? 'asc' : 'desc',
            orderBy: sortIndex,
        });
    };

    render() {
        const { classes, data } = this.props;
        return (
            <Paper elevation={1} className={classes.root}>
                <Table className={classes.table}>
                    {this.renderHeader(data, classes)}
                    {this.renderBody(data)}
                </Table>
            </Paper>
        );
    }

    renderHeader(data: ItemTableData, classes: any) {
        return (
            <TableHead>
                {data.title && data.title.length > 0 && this.renderHeaderTitleRow(data)}
                {this.renderHeaderRow(data, classes)}
            </TableHead>
        );
    }

    renderHeaderTitleRow(data: ItemTableData) {
        const numCols = data.header ? data.header.fields.length : 0;
        return (
            <TableRow>
                <TableCell colSpan={numCols} style={data.titleStyle || { textAlign: 'center' }}>
                    {data.title}
                </TableCell>
            </TableRow>
        );
    }

    sendSelectedChanged() {
        if (this.props.onSelectedChanged) {
            const selected: string[] = [];
            this.props.data.body.forEach((item, idx) => {
                if (this.state.itemSelected[idx] && item.key) {
                    selected.push(item.key);
                }
            });
            this.props.onSelectedChanged(selected);
        }
    }

    selectAllChanged(event: React.ChangeEvent<HTMLInputElement>) {
        const newSelected = [...this.state.itemSelected];
        this.props.data.body.forEach((item, idx) => (newSelected[idx] = event.target.checked));
        this.setState({ allChecked: event.target.checked, indeterminateChecked: false, itemSelected: newSelected });
    }

    onRowSelected() {
        let allSelected: boolean = true;
        let selectedTrueFound: boolean = false;
        let selectedFalseFound: boolean = false;
        this.props.data.body.forEach((item, idx) => {
            allSelected = allSelected && !!this.state.itemSelected[idx];
            selectedTrueFound = selectedTrueFound || !!this.state.itemSelected[idx];
            selectedFalseFound = selectedFalseFound || !this.state.itemSelected[idx];
        });
        this.setState({ allChecked: allSelected, indeterminateChecked: selectedTrueFound && selectedFalseFound });
        this.sendSelectedChanged();
    }

    componentDidUpdate(prevProps: ItemTableDataProps, prevState: ItemTableDataState) {
        let changed = prevProps.data.body.length !== this.props.data.body.length;
        if (!changed) {
            // length is the same, lets check contents of body
            prevProps.data.body.forEach(
                (item, idx) => (changed = changed || item.key !== this.props.data.body[idx]?.key)
            );
        }
        if (changed) {
            const temp = new Array(this.props.data.body.length);
            temp.fill(false);
            this.setState({ itemSelected: temp });
        }
        if (prevState.itemSelected !== this.state.itemSelected) {
            this.onRowSelected();
        }
    }

    renderHeaderRow(data: ItemTableData, classes: any) {
        return (
            <TableRow className={classes.tableRow}>
                {this.props.enableSelectRow && (
                    <TableCell>
                        <Checkbox
                            checked={this.state.allChecked}
                            indeterminate={this.state.indeterminateChecked}
                            onChange={(event) => this.selectAllChanged(event)}
                        />
                    </TableCell>
                )}
                {data.header.fields.map((dataField, idx) => (
                    <TableCell
                        className={classes.header}
                        key={dataField.key || idx}
                        style={dataField.style || this.props.defaultHeaderCellStyle}
                    >
                        {this.renderHeaderCell(dataField, idx)}
                    </TableCell>
                ))}
            </TableRow>
        );
    }

    renderHeaderCell(data: ItemTableDataField, index: number) {
        const { order, orderBy, enableSorting } = this.state;
        if (enableSorting) {
            return (
                <TableSortLabel
                    active={orderBy === index}
                    direction={order}
                    onClick={() => this.orderSetCallback(index)}
                >
                    {data.value}
                </TableSortLabel>
            );
        }
        return data.value;
    }

    renderBody(data: ItemTableData) {
        const { orderBy, order, enableSorting } = this.state;
        return (
            <TableBody>
                {this.getTableBodyContent(data, orderBy, order, enableSorting).map(
                    (dataRow: ItemTableDataRow, idx: number) => {
                        return this.renderBodyRow(dataRow, idx);
                    }
                )}
            </TableBody>
        );
    }

    getTableBodyContent(data: ItemTableData, orderBy: number, order: 'asc' | 'desc', enableSorting?: boolean) {
        if (enableSorting) {
            return stableSort(data.body, getSorting(order, orderBy));
        }
        return data.body;
    }

    renderBodyRow(dataRow: ItemTableDataRow, idx: number) {
        const { classes, enableDeletedRows } = this.props;
        const classArray = [this.props.onRowClick ? classes.tableRowHover : null, classes.tableRow];
        if (enableDeletedRows && dataRow.markedAsDeleted) {
            classArray.push(classes.deletedRow);
        }
        return (
            <TableRow
                key={dataRow.key || idx.toString()}
                className={classArray.join(' ')}
                onClick={() => this.handleClick(dataRow, idx)}
            >
                {this.props.enableSelectRow && (
                    <TableCell>
                        <Checkbox
                            checked={this.state.itemSelected.length > idx ? this.state.itemSelected[idx] : false}
                            onChange={(event) => {
                                const newSelected = [...this.state.itemSelected];
                                newSelected[idx] = event.target.checked;
                                this.setState({ itemSelected: newSelected });
                            }}
                        />
                    </TableCell>
                )}
                {dataRow.fields.map((dataField, fieldIdx) => {
                    if (dataField.id) {
                        return this.renderDocumentLinkField(dataField, fieldIdx);
                    }
                    if (isDocumentType(dataField.value)) {
                        return this.renderDocumentLinkField(dataField, fieldIdx);
                    }

                    return this.renderBodyField(dataField, fieldIdx);
                })}
            </TableRow>
        );
    }

    renderBodyField(dataField: ItemTableDataField, idx: number) {
        const { classes } = this.props;
        return (
            <TableCell
                key={dataField.key || idx.toString()}
                className={classes.tableCell}
                style={dataField.style || this.props.defaultBodyCellStyle}
                component={dataField.isRowHeader ? 'th' : 'td'}
                scope={dataField.isRowHeader ? 'row' : 'col'}
            >
                {dataField.value}
            </TableCell>
        );
    }

    renderDocumentLinkField(dataField: ItemTableDataField, idx: number) {
        const { classes } = this.props;
        const { value, id } = dataField;
        let document: QuoteDocument | null = null;
        let docLink: string = '';
        if (id) {
            document = { fileName: value.toString(), uuid: id, documentType: 'QUOTE' };
            docLink = getQuoteDocumentStorageUrl(document);
        }
        if (isQuoteDocument(value)) {
            document = value;
            docLink = getQuoteDocumentStorageUrl(document);
        }

        const display =
            document && docLink ? (
                <a
                    className={classes.tileAnchor}
                    href={docLink}
                    download={document ? document.fileName : null}
                    target={'_blank'}
                >
                    {document.fileName}
                </a>
            ) : (
                '--'
            );

        return (
            <TableCell
                key={dataField.key || idx.toString()}
                className={classes.tableCell}
                style={dataField.style || this.props.defaultBodyCellStyle}
                component={dataField.isRowHeader ? 'th' : 'td'}
                scope={dataField.isRowHeader ? 'row' : 'col'}
            >
                {display}
            </TableCell>
        );
    }

    handleClick(dataRow: ItemTableDataRow, idx: number) {
        if (this.props.onRowClick) {
            this.props.onRowClick(dataRow, idx);
        }
    }
}

export default ItemTable;
